import { Injectable } from '@angular/core';
// import posthog from 'posthog-js/dist/module.no-external';
// import 'posthog-js/dist/recorder';

declare var Penmate, posthog;

type CheckoutReplayContext = {
  letterId?: number | string;
  messageType?: string;
  shippingMethod?: string;
  paymentRequired?: boolean;
};

@Injectable({ providedIn: 'root' })
export class PosthogReplayService {
  private initialized = false;
  private startedForCheckout = false;
  private capturedForCheckout = false;
  private lastUserId: string | null = null;

  initIfNeeded() {
    try {
      if (this.initialized) return;
      if (typeof window === 'undefined') return;
      if (typeof Penmate === 'undefined' || !Penmate.env) return;
      if (!this.replayEnabled()) return;
      if (typeof posthog === 'undefined' || !posthog) return;

      const apiKey = Penmate.env.posthogProjectId;
      if (!apiKey || typeof apiKey !== 'string' || !apiKey.trim().length) return;

      const alreadyInitialized =
        !!(posthog && posthog.config && (posthog.config.token || posthog.config.api_token));

      if (!alreadyInitialized && posthog.init) {
        posthog.init(apiKey, {
          api_host: 'https://us.i.posthog.com',
          autocapture: false,
          capture_pageview: false,
          disable_session_recording: true,
          session_recording: {
            maskAllInputs: true,
            blockClass: 'ph-no-capture',
            maskTextClass: 'ph-mask',
          },
        });
      }

      this.initialized = true;
    } catch {
      // Never block checkout due to analytics/replay issues.
      this.initialized = false;
    }
  }

  maybeStartCheckoutReplay(user: any, ctx: CheckoutReplayContext = {}) {
    try {
      if (!this.replayEnabled()) return;
      this.initIfNeeded();
      if (!this.initialized) return;

      if (!posthog || !posthog.startSessionRecording) return;

      if (this.startedForCheckout) return;

      const userId = user && user.id ? String(user.id) : null;
      if (!userId) return;

      // Important privacy guard: if the app switches users within the same browser session/tab,
      // PostHog can keep the same `$session_id` and stitch recordings together. Resetting ensures
      // a new session/recording boundary for the new user.
      if (this.lastUserId && this.lastUserId !== userId) {
        try {
          if (posthog.reset) {
            posthog.reset(true);
          }
        } catch {
        }
        this.startedForCheckout = false;
        this.capturedForCheckout = false;
      }
      this.lastUserId = userId;

      const hasPurchasedCredits = user && user.hasPurchasedCredits === true;
      if (hasPurchasedCredits) return;

      const paymentRequired = ctx && ctx.paymentRequired === true;
      if (!paymentRequired) return;

      const effectiveSampleRate = this.sampleRateForUser(user);
      if (!this.shouldSampleCheckoutTrigger(userId, ctx, effectiveSampleRate)) return;

      if (posthog.sessionRecordingStarted && posthog.sessionRecordingStarted()) return;

      // Mark as started early to avoid double-starts when ConfirmOrder triggers multiple state updates
      // in quick succession (before posthog.sessionRecordingStarted() flips to true).
      this.startedForCheckout = true;

      try {
        posthog.identify(userId);
        // Match the working behavior observed earlier (and the event payloads PostHog shows in UI),
        // while still gating deterministically before this call.
        posthog.startSessionRecording({ sampling: true, event_trigger: true });
      } catch (e) {
        this.startedForCheckout = false;
        return;
      }

      // PostHog session replay UI is easiest to validate when at least one event is captured
      // from the browser during the recorded session. Keep volume low by only capturing when
      // a replay is actually started (i.e. sampled + payment required).
      try {
        if (posthog.capture) {
          if (this.capturedForCheckout) return;
          this.capturedForCheckout = true;

          const properties: any = {
            letter_id: ctx && ctx.letterId != null ? String(ctx.letterId) : null,
            message_type: ctx && ctx.messageType ? String(ctx.messageType) : null,
            shipping_method: ctx && ctx.shippingMethod ? String(ctx.shippingMethod) : null,
            payment_required: ctx && ctx.paymentRequired === true,
          };

          posthog.capture('pm_checkout_replay_started', properties);
        }
      } catch {
      }
    } catch {
      // Never block checkout due to analytics/replay issues.
      this.startedForCheckout = false;
      this.capturedForCheckout = false;
    }
  }

  stopCheckoutReplay() {
    try {
      if (!this.initialized) return;

      try {
        if (posthog && posthog.stopSessionRecording) {
          posthog.stopSessionRecording();
        }
      } catch (e) {
      }
    } catch {
    }

    this.startedForCheckout = false;
    this.capturedForCheckout = false;
  }

  private shouldSampleCheckoutTrigger(userId: string, ctx: CheckoutReplayContext, sampleRate: number): boolean {
    if (sampleRate <= 0) return false;
    if (sampleRate >= 1) return true;

    const month = this.currentMonthKey();
    const triggerId = ctx && ctx.letterId != null ? String(ctx.letterId) : 'unknown';
    const key = `pm_checkout_replay|${userId}|${triggerId}|${month}`;
    return this.hashToUnitInterval(key) < sampleRate;
  }

  private sampleRateForUser(user: any): number {
    const isNewUser =
      !!user &&
      user.hasSentLetter === false &&
      user.hasPurchasedCredits !== true;

    // Prioritize new, unconverted users when payment is required.
    if (isNewUser) {
      return this.sampleRateNewUserPaid();
    }
    return this.sampleRate();
  }

  private sampleRate(): number {
    try {
      if (typeof Penmate !== 'undefined' && Penmate.env) {
        const rawValue =
          Penmate.env.posthogReplayCheckoutSampleRate != null
            ? Penmate.env.posthogReplayCheckoutSampleRate
            : Penmate.env.posthogReplaySampleRate;
        if (rawValue == null) return 0.5;
        const raw = String(rawValue);
        const parsed = Number.parseFloat(raw);
        if (Number.isFinite(parsed)) {
          return Math.max(0, Math.min(1, parsed));
        }
      }
    } catch {
    }

    return 0.5;
  }

  private sampleRateNewUserPaid(): number {
    try {
      if (typeof Penmate !== 'undefined' && Penmate.env) {
        const rawValue =
          Penmate.env.posthogReplayNewUserCheckoutSampleRate != null
            ? Penmate.env.posthogReplayNewUserCheckoutSampleRate
            : Penmate.env.posthogReplaySampleRateNewUserPaid;
        if (rawValue == null) return 1;
        const raw = String(rawValue);
        const parsed = Number.parseFloat(raw);
        if (Number.isFinite(parsed)) {
          return Math.max(0, Math.min(1, parsed));
        }
      }
    } catch {
    }

    return 1;
  }

  private replayEnabled(): boolean {
    try {
      if (typeof Penmate === 'undefined' || !Penmate.env) return false;
      return this.parseBool(Penmate.env.posthogLegacyReplayEnabled, false);
    } catch {
      return false;
    }
  }

  private parseBool(value: any, fallback: boolean): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'on'].indexOf(normalized) >= 0) return true;
      if (['false', '0', 'no', 'off'].indexOf(normalized) >= 0) return false;
    }
    return fallback;
  }

  private currentMonthKey(): string {
    const d = new Date();
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  // FNV-1a 32-bit hash → [0, 1)
  private hashToUnitInterval(input: string): number {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      // 32-bit FNV_prime = 16777619
      hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
    }
    return hash / 0x100000000;
  }
}
